import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { cn } from '../../lib/utils'
import { MARKETPLACE_ENABLED } from '../../config/featureFlags'
import { useAuth } from '../../context/AuthContext'
import { ScrollArea } from '../ui/scroll-area'
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip'
import {
  LayoutDashboard,
  Building2,
  ShoppingBag,
  FileText,
  Target,
  Briefcase,
  BarChart3,
  Settings,
  CheckSquare,
  CalendarDays,
  ChevronRight,
  ChevronLeft,
  Package,
  Boxes,
  BookOpen,
  ImageIcon,
  Truck,
  BadgeCheck,
  PlusCircle,
  LayoutTemplate,
  KanbanSquare,
  Clock,
  UserCog,
  ClipboardCheck,
  Network,
} from 'lucide-react'

// ─── Types ─────────────────────────────────────────────────────────────────

export interface NavigationItem {
  name: string
  href: string
  icon: React.ComponentType<{ className?: string }>
  badge?: string | number
  children?: NavigationItem[]
  requiresFeature?: string
}

export interface NavigationSection {
  label?: string
  items: NavigationItem[]
}

interface SidebarProps {
  collapsed: boolean
  onToggle: () => void
}

// ─── Navigation Configuration ──────────────────────────────────────────────

const navigationSections: NavigationSection[] = [
  {
    label: 'Main',
    items: [
      { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
      { name: 'Customers', href: '/customers', icon: Building2 },
    ],
  },
  {
    label: 'Marketplace',
    items: [
      {
        name: 'Marketplace',
        href: '/marketplace',
        icon: ShoppingBag,
        requiresFeature: 'marketplace',
        children: [
          { name: 'Products', href: '/marketplace?tab=products', icon: Package },
          { name: 'Catalog', href: '/marketplace?tab=catalog', icon: Boxes },
          { name: 'Catalogues', href: '/marketplace?tab=catalogues', icon: BookOpen },
          { name: 'Images', href: '/marketplace?tab=images', icon: ImageIcon },
          { name: 'Distributors', href: '/marketplace?tab=distributors', icon: Truck },
          { name: 'Microsoft licences', href: '/marketplace?tab=microsoft-licences', icon: BadgeCheck },
        ],
      },
    ],
  },
  {
    label: 'Integrations',
    items: [
      { name: 'NinjaRMM', href: '/integrations/ninjaone', icon: Network, badge: 'New' },
    ],
  },
  {
    label: 'Applications',
    items: [
      { name: 'Tasks', href: '/tasks', icon: CheckSquare, badge: 'New' },
      {
        name: 'Contracts',
        href: '/contracts',
        icon: FileText,
        badge: 'New',
        requiresFeature: 'contracts',
      },
      { name: 'Calendar', href: '/calendar', icon: CalendarDays, badge: 'New' },
      {
        name: 'Approvals',
        href: '/approvals',
        icon: ClipboardCheck,
        badge: 'New',
        requiresFeature: 'recurringTasks',
      },
    ],
  },
  {
    label: 'Business',
    items: [
      {
        name: 'Proposals',
        href: '/proposals',
        icon: FileText,
        badge: 'New',
        children: [
          { name: 'All Proposals', href: '/proposals', icon: FileText },
          { name: 'Create Proposal', href: '/proposals/create', icon: PlusCircle },
          { name: 'Templates', href: '/proposals/templates', icon: LayoutTemplate },
        ],
      },
      {
        name: 'Leads',
        href: '/leads',
        icon: Target,
        badge: 'New',
        children: [
          { name: 'All Leads', href: '/leads', icon: Target },
          { name: 'Pipeline View', href: '/leads/pipeline', icon: KanbanSquare },
        ],
      },
      {
        name: 'Projects',
        href: '/projects',
        icon: Briefcase,
        badge: 'New',
        children: [
          { name: 'All Projects', href: '/projects', icon: Briefcase },
          { name: 'Timeline View', href: '/projects/timeline', icon: Clock },
        ],
      },
    ],
  },
  {
    label: 'Analytics',
    items: [{ name: 'Reports', href: '/reports', icon: BarChart3 }],
  },
  {
    label: 'System',
    items: [
      { name: 'Settings', href: '/settings', icon: Settings },
      { name: 'User Settings', href: '/user-settings', icon: UserCog },
    ],
  },
]

// ─── Filter navigation based on feature flags ──────────────────────────────

function filterNavigation(
  sections: NavigationSection[],
  partnerFeatures: Record<string, boolean> | undefined,
): NavigationSection[] {
  return sections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => {
        if (!item.requiresFeature) return true
        // Marketplace is gated by a global build-time flag, not a per-partner flag.
        if (item.requiresFeature === 'marketplace') return MARKETPLACE_ENABLED
        // Everything else is a per-partner feature flag from PartnerFeatures.
        return partnerFeatures?.[item.requiresFeature] === true
      }),
    }))
    .filter((section) => section.items.length > 0)
}

// ─── Active matching helpers ───────────────────────────────────────────────

function isItemActive(item: NavigationItem, location: ReturnType<typeof useLocation>): boolean {
  const [itemPath, itemSearch] = item.href.split('?')
  if (itemSearch) {
    const pathMatch = location.pathname === itemPath || location.pathname.startsWith(itemPath)
    const itemParams = new URLSearchParams(itemSearch)
    const locationParams = new URLSearchParams(location.search)
    const paramsMatch = Array.from(itemParams.entries()).every(
      ([key, value]) => locationParams.get(key) === value,
    )
    return pathMatch && paramsMatch
  }
  return (
    location.pathname === item.href ||
    (item.href !== '/dashboard' && item.href !== '/' && location.pathname.startsWith(itemPath))
  )
}

// ─── Section header ─────────────────────────────────────────────────────────
// Hairline divider + uppercase mono label. `isFirst` skips the divider so the
// top section sits flush against the gradient header.

const NavSection: React.FC<{
  label?: string
  isFirst?: boolean
  collapsed?: boolean
  children: React.ReactNode
}> = ({ label, isFirst, collapsed, children }) => (
  <div className={isFirst ? '' : 'mt-3 pt-3 border-t border-slate-200/80'}>
    {label && !collapsed && (
      <div className="px-3 mb-1 font-mono text-[11px] uppercase tracking-[0.14em] text-slate-400 font-semibold">
        {label}
      </div>
    )}
    <div className="space-y-0">{children}</div>
  </div>
)

// ─── Badge pill ────────────────────────────────────────────────────────────

const NavBadge: React.FC<{ value: string | number }> = ({ value }) => {
  const isNumeric = typeof value === 'number'
  return (
    <span
      className={cn(
        'text-[10px] font-mono font-medium h-[18px] min-w-[18px] px-1.5 rounded-full inline-flex items-center justify-center',
        isNumeric ? 'bg-orange-600 text-white' : 'bg-orange-100 text-orange-700',
      )}
    >
      {isNumeric && typeof value === 'number' && value > 99 ? '99+' : value}
    </span>
  )
}

// ─── Leaf row (top-level destination or indented child) ────────────────────

interface NavRowProps {
  title: string
  href: string
  icon?: React.ComponentType<{ className?: string }>
  isActive: boolean
  indented?: boolean
  badge?: string | number
  collapsed?: boolean
}

const NavRow: React.FC<NavRowProps> = ({
  title,
  href,
  icon: Icon,
  isActive,
  indented,
  badge,
  collapsed,
}) => {
  if (collapsed && !indented && Icon) {
    return (
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <Link
            to={href}
            className={cn(
              'w-full flex items-center justify-center h-9 transition-colors relative',
              isActive
                ? 'bg-orange-50 text-orange-700'
                : 'text-slate-600 hover:bg-slate-50',
            )}
          >
            <Icon className="w-[17px] h-[17px]" />
            {badge !== undefined && (
              <span className="absolute top-1 right-1.5 w-1.5 h-1.5 rounded-full bg-orange-500" />
            )}
          </Link>
        </TooltipTrigger>
        <TooltipContent side="right" className="font-medium">
          {title}
        </TooltipContent>
      </Tooltip>
    )
  }

  return (
    <Link
      to={href}
      // Child rows get a tighter pl-7 so they don't feel buried, plus a
      // bullet marker in the icon slot — slate-300 idle / slate-500 hover /
      // slate-900 active — to match the active treatment of parent rows
      // without competing with their iconography.
      style={indented ? { paddingLeft: 28 } : undefined}
      className={cn(
        'group w-full flex items-center gap-2.5 h-10 pr-2 text-sm text-left transition-colors',
        !indented && 'pl-2.5',
        isActive
          ? 'bg-orange-50 text-slate-900 font-semibold'
          : 'text-slate-700 hover:bg-slate-50',
      )}
    >
      {Icon && !indented && (
        <span
          className={cn(
            'w-5 h-5 flex items-center justify-center flex-shrink-0',
            isActive ? 'text-orange-700' : 'text-slate-500',
          )}
        >
          <Icon className="w-5 h-5" />
        </span>
      )}
      {indented && (
        <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
          <span
            className={cn(
              'block rounded-full transition-colors',
              isActive
                ? 'w-1.5 h-1.5 bg-slate-900'
                : 'w-1 h-1 bg-slate-300 group-hover:bg-slate-500',
            )}
          />
        </span>
      )}
      <span className="flex-1 truncate">{title}</span>
      {badge !== undefined && <NavBadge value={badge} />}
    </Link>
  )
}

// ─── Group header (expandable parent with children) ────────────────────────

interface NavGroupRowProps {
  title: string
  href: string
  icon: React.ComponentType<{ className?: string }>
  isExpanded: boolean
  hasActiveChild: boolean
  badge?: string | number
  collapsed?: boolean
  onToggle: () => void
}

const NavGroupRow: React.FC<NavGroupRowProps> = ({
  title,
  href,
  icon: Icon,
  isExpanded,
  hasActiveChild,
  badge,
  collapsed,
  onToggle,
}) => {
  if (collapsed) {
    // In collapsed mode the group acts as a direct link to its primary href —
    // children disappear and the icon-only rail can't show the chevron.
    return (
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <Link
            to={href}
            className={cn(
              'w-full flex items-center justify-center h-9 transition-colors relative',
              hasActiveChild
                ? 'bg-orange-50 text-orange-700'
                : 'text-slate-600 hover:bg-slate-50',
            )}
          >
            <Icon className="w-[17px] h-[17px]" />
            {badge !== undefined && (
              <span className="absolute top-1 right-1.5 w-1.5 h-1.5 rounded-full bg-orange-500" />
            )}
          </Link>
        </TooltipTrigger>
        <TooltipContent side="right" className="font-medium">
          {title}
        </TooltipContent>
      </Tooltip>
    )
  }

  // Expanded mode — group row never carries the chip-style active fill; the
  // active child below carries the highlight on its own. Group still gets a
  // subtle text/icon weight bump when one of its children is active so the
  // user can see "this section has my page in it" at a glance.
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={isExpanded}
      className="w-full flex items-center gap-2.5 h-10 pl-2.5 pr-2 text-sm text-left transition-colors text-slate-700 hover:bg-slate-50 bg-transparent border-0"
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={cn(
          'w-4 h-4 flex-shrink-0 transition-transform',
          hasActiveChild ? 'text-slate-700' : 'text-slate-400',
          !isExpanded && '-rotate-90',
        )}
      >
        <polyline points="6 9 12 15 18 9" />
      </svg>
      <span
        className={cn(
          'w-5 h-5 flex items-center justify-center flex-shrink-0',
          hasActiveChild ? 'text-orange-700' : 'text-slate-500',
        )}
      >
        <Icon className="w-5 h-5" />
      </span>
      <span className={cn('flex-1 truncate', hasActiveChild && 'font-semibold text-slate-900')}>
        {title}
      </span>
      {badge !== undefined && <NavBadge value={badge} />}
    </button>
  )
}

// ─── Sidebar Component ─────────────────────────────────────────────────────

export default function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const { partnerAuthContext, partner, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const filteredSections = useMemo(
    () => filterNavigation(navigationSections, partnerAuthContext?.partnerFeatures),
    [partnerAuthContext?.partnerFeatures],
  )

  // Track which groups are open. Auto-expand any group containing the active
  // route so deep-linking doesn't strand the user looking at a collapsed
  // header. Manually-opened groups stay open across navigation.
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => {
    const initial = new Set<string>()
    filteredSections.forEach((section) =>
      section.items.forEach((item) => {
        if (item.children?.some((child) => isItemActive(child, location))) {
          initial.add(item.href)
        }
      }),
    )
    return initial
  })

  useEffect(() => {
    setExpandedGroups((prev) => {
      const next = new Set(prev)
      filteredSections.forEach((section) =>
        section.items.forEach((item) => {
          if (item.children?.some((child) => isItemActive(child, location))) {
            next.add(item.href)
          }
        }),
      )
      return next.size === prev.size && Array.from(next).every((g) => prev.has(g)) ? prev : next
    })
  }, [location, filteredSections])

  const toggleGroup = useCallback((href: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(href)) next.delete(href)
      else next.add(href)
      return next
    })
  }, [])

  // Profile dropdown — pinned footer affordance.
  const [profileOpen, setProfileOpen] = useState(false)
  useEffect(() => {
    if (!profileOpen) return
    const onDoc = (e: MouseEvent) => {
      const t = e.target as HTMLElement
      if (!t.closest('[data-profile-menu]')) setProfileOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [profileOpen])

  const partnerName = partner?.partnerName || 'Partner'
  const partnerInitial = (partnerName.charAt(0) || 'P').toUpperCase()

  return (
    <aside
      className={cn(
        'hidden lg:flex flex-col border-r border-slate-200 bg-white transition-[width] duration-200 ease-in-out shrink-0',
        collapsed ? 'w-[56px]' : 'w-[248px]',
      )}
    >
      {/* Branded top zone — orange gradient. 53px to match the height of the
          existing TopBar's brand strip so the two surfaces feel related. */}
      <div
        className={cn(
          'h-[53px] flex-shrink-0 flex items-center shrink-0',
          collapsed ? 'justify-center px-2' : 'px-3',
        )}
        style={{ background: 'linear-gradient(135deg, #F97316 0%, #FB923C 100%)' }}
      >
        {collapsed ? (
          <div className="w-7 h-7 rounded-md bg-white/15 flex items-center justify-center text-white font-bold text-[13px] tracking-tight">
            A
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-white/15 flex items-center justify-center text-white font-bold text-sm shadow-sm">
              A
            </div>
            <div className="leading-tight">
              <span className="text-base font-semibold text-white tracking-tight">Aitryx</span>
              <span className="text-sm text-white/75 ml-1">Partner</span>
            </div>
          </div>
        )}
      </div>
      <div className="h-[2px] bg-white shrink-0" />

      {/* Main nav — slim flat rows grouped into labelled sections. Scrolls
          if it overflows; pinned footer below stays put. */}
      <ScrollArea className="flex-1 min-h-0">
        <nav className="pt-1.5 pb-2 px-2">
          {filteredSections.map((section, idx) => (
            <NavSection
              key={section.label ?? `__section-${idx}`}
              label={section.label}
              isFirst={idx === 0}
              collapsed={collapsed}
            >
              {section.items.map((item) => {
                const itemActive = isItemActive(item, location)
                const hasChildren = (item.children?.length ?? 0) > 0
                const childActive =
                  hasChildren &&
                  item.children!.some((child) => isItemActive(child, location))
                const isExpanded = expandedGroups.has(item.href)

                if (hasChildren) {
                  return (
                    <React.Fragment key={item.href}>
                      <NavGroupRow
                        title={item.name}
                        href={item.href}
                        icon={item.icon}
                        isExpanded={isExpanded}
                        hasActiveChild={itemActive || childActive}
                        badge={item.badge}
                        collapsed={collapsed}
                        onToggle={() =>
                          collapsed ? navigate(item.href) : toggleGroup(item.href)
                        }
                      />
                      {!collapsed && isExpanded &&
                        item.children!.map((child) => (
                          <NavRow
                            key={child.href}
                            title={child.name}
                            href={child.href}
                            isActive={isItemActive(child, location)}
                            indented
                            badge={child.badge}
                          />
                        ))}
                    </React.Fragment>
                  )
                }

                return (
                  <NavRow
                    key={item.href}
                    title={item.name}
                    href={item.href}
                    icon={item.icon}
                    isActive={itemActive}
                    badge={item.badge}
                    collapsed={collapsed}
                  />
                )
              })}
            </NavSection>
          ))}
        </nav>
      </ScrollArea>

      {/* Pinned footer — tinted slate-50 with a heavier divider + soft inset
          shadow so it reads as a distinct fixed footer above the scrollable
          nav. Profile dropdown + collapse toggle live here. */}
      <div className="shrink-0 border-t-2 border-slate-200 bg-slate-50 shadow-[0_-4px_8px_-6px_rgba(15,23,42,0.08)]">
        {/* Profile button — opens dropdown above with User Settings + Logout. */}
        <div className="relative" data-profile-menu>
          {!collapsed ? (
            <button
              type="button"
              onClick={() => setProfileOpen((o) => !o)}
              className="w-full flex items-center gap-2.5 h-14 px-3 hover:bg-slate-100 transition-colors text-left bg-transparent border-0"
            >
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-orange-500 to-amber-400 text-white flex items-center justify-center text-[13px] font-semibold flex-shrink-0 shadow-sm">
                {partnerInitial}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold text-slate-900 truncate leading-tight">
                  {partnerName}
                </div>
                <div className="text-[11px] text-slate-500 truncate leading-tight mt-0.5">
                  Partner
                </div>
              </div>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-4 h-4 text-slate-400 flex-shrink-0"
              >
                <circle cx="12" cy="5" r="1" />
                <circle cx="12" cy="12" r="1" />
                <circle cx="12" cy="19" r="1" />
              </svg>
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setProfileOpen((o) => !o)}
              title={partnerName}
              className="w-full flex items-center justify-center h-14 hover:bg-slate-100 transition-colors bg-transparent border-0"
            >
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-orange-500 to-amber-400 text-white flex items-center justify-center text-[13px] font-semibold shadow-sm">
                {partnerInitial}
              </div>
            </button>
          )}

          {profileOpen && (
            <div
              className={cn(
                'absolute bottom-full mb-1.5 bg-white rounded-md ring-1 ring-slate-900/5 shadow-[0_12px_32px_-8px_rgba(15,23,42,0.18)] border border-slate-200 py-1 z-30 text-slate-700',
                collapsed ? 'left-full ml-2 w-56' : 'left-1 right-1',
              )}
            >
              <div className="px-3 py-2.5 border-b border-slate-100">
                <div className="text-[13px] font-semibold text-slate-900 truncate">
                  {partnerName}
                </div>
                {partner?.partnerId && (
                  <div className="text-[11px] text-slate-500 truncate font-mono">
                    {partner.partnerId}
                  </div>
                )}
              </div>
              <div className="py-1">
                <button
                  type="button"
                  onClick={() => {
                    setProfileOpen(false)
                    navigate('/settings')
                  }}
                  className="w-full flex items-center gap-2.5 h-9 px-3 text-[13px] hover:bg-slate-50 text-left bg-transparent border-0 text-slate-700 transition-colors"
                >
                  <Settings className="w-4 h-4 text-slate-500" />
                  <span>Settings</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setProfileOpen(false)
                    navigate('/user-settings')
                  }}
                  className="w-full flex items-center gap-2.5 h-9 px-3 text-[13px] hover:bg-slate-50 text-left bg-transparent border-0 text-slate-700 transition-colors"
                >
                  <UserCog className="w-4 h-4 text-slate-500" />
                  <span>User Settings</span>
                </button>
              </div>
              <div className="border-t border-slate-100 py-1">
                <button
                  type="button"
                  onClick={() => {
                    setProfileOpen(false)
                    logout()
                  }}
                  className="w-full flex items-center gap-2.5 h-9 px-3 text-[13px] hover:bg-rose-50 text-left bg-transparent border-0 text-rose-600 transition-colors"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="w-4 h-4 text-rose-500"
                  >
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                    <polyline points="16 17 21 12 16 7" />
                    <line x1="21" y1="12" x2="9" y2="12" />
                  </svg>
                  <span>Sign out</span>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Collapse toggle — preserved (TopBar / AppLayout still drive the
            collapse state via localStorage), but restyled to sit above the
            footer profile in a separate slim row. */}
        <div className="border-t border-slate-200">
          <button
            onClick={onToggle}
            className={cn(
              'w-full flex items-center h-9 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors bg-transparent border-0',
              collapsed ? 'justify-center px-2' : 'justify-center gap-2 px-3 text-xs font-medium',
            )}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <>
                <ChevronLeft className="h-4 w-4" />
                <span>Collapse</span>
              </>
            )}
          </button>
        </div>
      </div>
    </aside>
  )
}
